// MarketLimitArticle.jsx — slug-specific layout for /insights/market-order-vs-limit-order.
// Renders the article body VERBATIM (from the DB record) inside a centered reading column,
// and layers in in-code SVG + styled panels/cards that restate what the article already says.
// No external images. No AI-generated art. No new numbers — every figure appears in the body.
import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, AlertTriangle, Clock, Calendar } from "lucide-react";
import { mdToHtml } from "@/public/md";

/* ------------------------------------------------------------------ helpers */

// Split the body markdown into {heading, mdBody} sections keyed by ## headings.
// Preserves the body VERBATIM — nothing is rewritten; we only partition it.
function splitByH2(md) {
  const lines = (md || "").replace(/\r/g, "").split("\n");
  const sections = [];
  let cur = { heading: null, body: [] };
  for (const line of lines) {
    const m = line.match(/^##\s+(.*)$/);
    if (m) {
      if (cur.heading || cur.body.length) sections.push(cur);
      cur = { heading: m[1].trim(), body: [] };
    } else {
      cur.body.push(line);
    }
  }
  if (cur.heading || cur.body.length) sections.push(cur);
  return sections.map((s) => ({ heading: s.heading, mdBody: s.body.join("\n").trim() }));
}

function readMinutes(md) {
  // Round up — a reader never finishes earlier than the label promises. ~220 wpm.
  const words = (md || "").split(/\s+/).filter(Boolean).length;
  return Math.max(3, Math.ceil(words / 220));
}

function formatPublished(iso) {
  if (!iso) return "";
  try {
    // Render in UTC so a datePublished of "2026-11-02T00:00:00+00:00" shows Nov 2 everywhere
    // (US-eastern viewers would otherwise see Nov 1 and disagree with the JSON-LD + sitemap).
    const d = new Date(iso);
    return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric", timeZone: "UTC" });
  } catch { return ""; }
}

/* ---------------------------------------------------------- in-code visuals */

// Short-answer callout — frames the opening section without changing a word.
function ShortAnswerCallout({ children }) {
  return (
    <aside
      data-testid="insight-callout-short-answer"
      className="not-prose my-10 rounded-2xl border border-tc-coach/40 bg-gradient-to-br from-tc-coach/[0.09] to-tc-coach/[0.02] p-6 sm:p-7 shadow-[0_0_32px_rgba(6,182,212,0.08)]"
    >
      <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tc-coach">
        <span className="h-1.5 w-1.5 rounded-full bg-tc-coach" aria-hidden="true" />
        The short answer
      </div>
      <div className="tc-reading-prose mt-4">{children}</div>
    </aside>
  );
}

// Bid / Ask / Spread diagram — numbers come from the article ($50.00 bid, $50.10 ask, $0.10 spread).
// Only the two prices the article names are plotted (removes intermediate ticks that otherwise
// get crushed to unreadable sub-pixel text at phone widths) and the descriptive sub-label is
// moved into the figure caption so there is no risk of SVG label clipping at 390px.
function BidAskSpreadDiagram() {
  return (
    <figure
      data-testid="insight-svg-spread"
      className="not-prose my-10 rounded-2xl border border-white/10 bg-tc-card/60 p-5 sm:p-7"
    >
      <figcaption className="font-mono text-[11px] uppercase tracking-[0.18em] text-tc-sub">
        How the spread sits in the quote
      </figcaption>
      <svg
        viewBox="0 0 400 200"
        role="img"
        aria-label="Bid 50.00, Ask 50.10, Spread 0.10"
        preserveAspectRatio="xMidYMid meet"
        className="mt-5 block w-full h-auto"
      >
        <defs>
          <linearGradient id="spreadGrad" x1="0" x2="1" y1="0" y2="0">
            <stop offset="0" stopColor="#F59E0B" stopOpacity="0.65" />
            <stop offset="1" stopColor="#06B6D4" stopOpacity="0.65" />
          </linearGradient>
        </defs>

        {/* axis across the plot */}
        <line x1="40" y1="130" x2="360" y2="130" stroke="#334155" strokeWidth="1.4" />

        {/* spread band between the two labeled prices */}
        <rect x="120" y="100" width="160" height="30" fill="url(#spreadGrad)" opacity="0.45" />
        <text x="200" y="86" textAnchor="middle" fill="#E2E8F0" fontFamily="Space Grotesk, sans-serif" fontSize="15" fontWeight="700">
          Spread · $0.10
        </text>

        {/* bid marker */}
        <circle cx="120" cy="130" r="8" fill="#F59E0B" />
        <line x1="120" y1="52" x2="120" y2="122" stroke="#F59E0B" strokeDasharray="3 3" />
        <text x="120" y="42" textAnchor="middle" fill="#F59E0B" fontFamily="JetBrains Mono, monospace" fontSize="14" fontWeight="700">
          BID · $50.00
        </text>
        <text x="120" y="170" textAnchor="middle" fill="#94A3B8" fontFamily="JetBrains Mono, monospace" fontSize="13">
          $50.00
        </text>

        {/* ask marker */}
        <circle cx="280" cy="130" r="8" fill="#06B6D4" />
        <line x1="280" y1="52" x2="280" y2="122" stroke="#06B6D4" strokeDasharray="3 3" />
        <text x="280" y="42" textAnchor="middle" fill="#06B6D4" fontFamily="JetBrains Mono, monospace" fontSize="14" fontWeight="700">
          ASK · $50.10
        </text>
        <text x="280" y="170" textAnchor="middle" fill="#94A3B8" fontFamily="JetBrains Mono, monospace" fontSize="13">
          $50.10
        </text>
      </svg>
      <p className="mt-4 text-[13px] text-tc-sub leading-relaxed">
        The bid is the highest a buyer will pay; the ask is the lowest a seller will accept. A market
        order crosses the spread and takes the ask (~$50.10). A limit order below the ask waits for a
        seller to meet your price.
      </p>
    </figure>
  );
}

// Side-by-side comparison cards — restates the article's definitions only.
function MarketVsLimitCards() {
  return (
    <div
      data-testid="insight-compare-cards"
      className="not-prose my-10 grid gap-4 sm:gap-5 md:grid-cols-2"
    >
      <div className="rounded-2xl border border-amber-500/30 bg-amber-500/[0.04] p-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber-400">Market order</div>
        <h3 className="mt-2 font-heading text-xl font-semibold text-tc-txt">Buy now, best price available.</h3>
        <dl className="mt-5 space-y-3 text-[15px]">
          <div className="flex gap-3">
            <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-widest text-amber-400/90">You get</dt>
            <dd className="text-tc-txt">Speed and certainty of execution</dd>
          </div>
          <div className="flex gap-3">
            <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-widest text-amber-400/90">You give up</dt>
            <dd className="text-tc-sub">Certainty of price (slippage happens)</dd>
          </div>
        </dl>
      </div>
      <div className="rounded-2xl border border-tc-coach/40 bg-tc-coach/[0.05] p-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-tc-coach">Limit order</div>
        <h3 className="mt-2 font-heading text-xl font-semibold text-tc-txt">Buy only at this price or better.</h3>
        <dl className="mt-5 space-y-3 text-[15px]">
          <div className="flex gap-3">
            <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-widest text-tc-coach">You get</dt>
            <dd className="text-tc-txt">Certainty of price</dd>
          </div>
          <div className="flex gap-3">
            <dt className="w-28 shrink-0 font-mono text-[11px] uppercase tracking-widest text-tc-coach">You give up</dt>
            <dd className="text-tc-sub">Certainty that the trade happens</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

// Worked example panel — restates only numbers already in the article.
function WorkedExamplePanel() {
  return (
    <aside
      data-testid="insight-worked-example"
      className="not-prose my-10 rounded-2xl border border-white/10 bg-tc-card/70 p-6 sm:p-7"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-tc-sub">Worked example</div>
      <div className="mt-4 grid gap-5 sm:grid-cols-3">
        <div className="rounded-xl border border-white/5 bg-black/20 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-tc-muted">Quote</div>
          <div className="mt-1 font-mono text-base text-tc-txt">$50.00 / $50.10</div>
          <div className="mt-1 text-[12px] text-tc-sub">10-cent spread</div>
        </div>
        <div className="rounded-xl border border-white/5 bg-black/20 p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-tc-muted">Round trip cost</div>
          <div className="mt-1 font-mono text-base text-tc-txt">$0.10 / share</div>
          <div className="mt-1 text-[12px] text-tc-sub">before the price moves at all</div>
        </div>
        <div className="rounded-xl border border-tc-coach/30 bg-tc-coach/[0.06] p-4">
          <div className="font-mono text-[10px] uppercase tracking-widest text-tc-coach">100 shares</div>
          <div className="mt-1 font-mono text-base text-tc-coach font-semibold">$0.10 × 100 = $10</div>
          <div className="mt-1 text-[12px] text-tc-sub">invisible unless you look</div>
        </div>
      </div>
    </aside>
  );
}

// Three numbered pro-question cards.
function ProQuestionsCards() {
  const items = [
    { n: 1, title: "How fast is this moving?", gist: "In calm, heavily traded stocks, market-order slippage is usually tiny." },
    { n: 2, title: "How wide is the spread?", gist: "The wider the spread, the stronger the case for a limit order." },
    { n: 3, title: "What's my plan if I'm wrong about the price?", gist: "A limit order is a discipline tool — decide your price before emotion shows up." },
  ];
  return (
    <div data-testid="insight-pro-questions" className="not-prose my-10 grid gap-4 sm:grid-cols-3">
      {items.map((it) => (
        <div key={it.n} className="rounded-2xl border border-white/10 bg-tc-card/70 p-5">
          <div className="flex items-center gap-3">
            <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-tc-coach/40 bg-tc-coach/10 font-mono text-sm font-bold text-tc-coach">
              {it.n}
            </span>
            <span className="font-mono text-[10px] uppercase tracking-widest text-tc-muted">Question</span>
          </div>
          <h3 className="mt-4 font-heading text-base font-semibold text-tc-txt leading-snug">{it.title}</h3>
          <p className="mt-2 text-[14px] text-tc-sub leading-relaxed">{it.gist}</p>
        </div>
      ))}
    </div>
  );
}

// Two beginner-mistake warning cards.
function BeginnerMistakesCards() {
  const items = [
    {
      title: "Treating the market order as the default for everything",
      body: "It's the default button in most apps, so most beginners never make an active choice. In a fast or illiquid stock, that can mean paying far more than the price they saw.",
    },
    {
      title: "Setting a limit order and forgetting what it means",
      body: "A limit that never fills isn't a failed trade — it's a decision that worked. Chasing the price upward turns a disciplined plan into an emotional one.",
    },
  ];
  return (
    <div data-testid="insight-mistakes" className="not-prose my-10 grid gap-4 md:grid-cols-2">
      {items.map((it, i) => (
        <div key={i} className="rounded-2xl border border-tc-bear/30 bg-tc-bear/[0.04] p-5">
          <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-tc-bear">
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Mistake {i + 1}
          </div>
          <h3 className="mt-3 font-heading text-base font-semibold text-tc-txt leading-snug">{it.title}</h3>
          <p className="mt-2 text-[14px] text-tc-sub leading-relaxed">{it.body}</p>
        </div>
      ))}
    </div>
  );
}

// Closing CTA — designed restatement of the two links already in the body.
function ClosingCTAPanel() {
  return (
    <aside
      data-testid="insight-cta-panel"
      className="not-prose my-10 rounded-2xl border border-tc-coach/40 bg-gradient-to-br from-tc-coach/[0.1] via-transparent to-tc-coach/[0.04] p-7 sm:p-8"
    >
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-tc-coach">Start learning</div>
      <h3 className="mt-3 font-heading text-xl sm:text-2xl font-bold text-tc-txt leading-snug">
        Learn the mechanics before you need them.
      </h3>
      <p className="mt-3 text-[15px] text-tc-sub leading-relaxed">
        5 AI analyses a month, the full Coach curriculum, and a competency assessment — no card required.
      </p>
      <div className="mt-5 flex flex-wrap gap-3">
        <Link
          to="/signup"
          data-testid="insight-cta-primary"
          className="inline-flex items-center gap-2 rounded-xl bg-tc-coach px-5 py-2.5 text-sm font-semibold text-tc-bg hover:opacity-90 hover:shadow-[0_0_28px_rgba(6,182,212,0.35)] transition"
        >
          Start Free <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/pricing"
          data-testid="insight-cta-secondary"
          className="inline-flex items-center gap-2 rounded-xl border border-white/15 px-5 py-2.5 text-sm font-semibold text-tc-txt hover:border-tc-coach/50 hover:text-tc-coach transition"
        >
          See plans
        </Link>
      </div>
    </aside>
  );
}

// Designed FAQ.
function DesignedFaq({ faq }) {
  if (!faq?.length) return null;
  return (
    <section data-testid="insight-faq" className="mt-14 not-prose">
      <div className="flex items-center gap-3">
        <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
        <h2 className="font-heading text-xl font-bold text-tc-txt tracking-tight">FAQ</h2>
        <span className="h-px flex-1 bg-white/10" aria-hidden="true" />
      </div>
      <dl className="mt-6 grid gap-3">
        {faq.map(([q, ans], i) => (
          <div
            key={i}
            className="rounded-xl border border-white/10 bg-tc-card/60 p-5 hover:border-tc-coach/40 transition"
          >
            <dt className="flex items-start gap-3 font-heading text-[15px] font-semibold text-tc-txt leading-snug">
              <span className="font-mono text-xs text-tc-coach pt-0.5">Q{i + 1}.</span>
              <span>{q}</span>
            </dt>
            <dd className="mt-2 pl-7 text-[15px] text-tc-sub leading-relaxed">{ans}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

/* ----------------------------------------------------------------- template */

const SECTION_VISUALS_AFTER = {
  // keyed by h2 heading text — visuals rendered AFTER the section's verbatim body
  "What actually happens when you place an order": () => (
    <>
      <BidAskSpreadDiagram />
      <MarketVsLimitCards />
    </>
  ),
  "A worked example": () => <WorkedExamplePanel />,
  "How professionals decide which to use": () => <ProQuestionsCards />,
  "The two mistakes beginners make with orders": () => <BeginnerMistakesCards />,
  "Learn the mechanics before you need them": () => <ClosingCTAPanel />,
};

export default function MarketLimitArticle({ article }) {
  const sections = splitByH2(article.body_markdown);
  const minutes = readMinutes(article.body_markdown);
  const published = formatPublished(article.published_at);

  return (
    <article data-testid="market-limit-article" className="pt-32 sm:pt-40 pb-20 px-5 sm:px-6">
      <div className="mx-auto w-full max-w-[760px]">
        {/* Header */}
        <header className="not-prose">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-tc-coach" data-testid="insight-cluster">
            {article.topic_cluster}
          </div>
          <h1
            data-testid="insight-title"
            className="mt-4 font-heading text-3xl sm:text-[2.5rem] font-extrabold tracking-tight leading-[1.1] text-tc-txt"
          >
            {article.title}
          </h1>
          <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[12px] text-tc-muted">
            {published && (
              <span className="inline-flex items-center gap-1.5" data-testid="insight-published">
                <Calendar className="h-3.5 w-3.5" aria-hidden="true" />
                Published <time dateTime={article.published_at}>{published}</time>
              </span>
            )}
            <span className="inline-flex items-center gap-1.5" data-testid="insight-read-time">
              <Clock className="h-3.5 w-3.5" aria-hidden="true" />
              {minutes} min read
            </span>
          </div>
          <div className="mt-6 h-px w-full bg-gradient-to-r from-tc-coach/40 via-white/10 to-transparent" aria-hidden="true" />
        </header>

        {/* Sections — body text VERBATIM, visuals interleaved */}
        <div className="mt-10 space-y-2">
          {sections.map((s, i) => {
            if (!s.heading && !s.mdBody) return null;
            const bodyHtml = { __html: mdToHtml(s.mdBody) };
            const AfterVisual = SECTION_VISUALS_AFTER[s.heading];

            // Section 1 ("The short answer") — body rendered INSIDE the callout, verbatim.
            if (s.heading === "The short answer (read this first)") {
              return (
                <ShortAnswerCallout key={i}>
                  <div dangerouslySetInnerHTML={bodyHtml} />
                </ShortAnswerCallout>
              );
            }

            return (
              <section key={i} data-testid={`insight-section-${i}`}>
                {s.heading && (
                  <h2 className="font-heading text-[1.6rem] sm:text-[1.75rem] font-bold text-tc-txt tracking-tight leading-tight mt-10 mb-3">
                    {s.heading}
                  </h2>
                )}
                {s.mdBody && (
                  <div
                    className="tc-reading-prose"
                    data-testid={`insight-body-${i}`}
                    dangerouslySetInnerHTML={bodyHtml}
                  />
                )}
                {AfterVisual && <AfterVisual />}
              </section>
            );
          })}
        </div>

        {/* Designed FAQ */}
        <DesignedFaq faq={article.faq} />

        {/* Footer */}
        <footer className="mt-14 not-prose border-t border-white/10 pt-6">
          <p className="font-mono text-[11px] leading-relaxed text-tc-muted">
            Educational analysis. Not financial advice; no real-money execution; no guaranteed outcomes.
          </p>
          <Link
            to="/insights"
            data-testid="insight-back"
            className="mt-6 inline-flex items-center gap-1.5 text-sm text-tc-coach hover:underline"
          >
            ← All insights
          </Link>
        </footer>
      </div>
    </article>
  );
}
